import {
    CreateCourierOrderRequest,
    SteadfastCourierResponse,
} from '../types';
import { apiService } from './api';
import { getCustomerFullName } from '../utils/customerUtils';
import { tryNormalizeBdPhone } from '../utils/phone';

/** One webhook call Steadfast made to us, and what we did with it. */
export interface CourierWebhookEvent {
    event_id: string;
    provider: string;
    notification_type: string;
    idempotency_key?: string | null;
    consignment_id?: string | null;
    invoice?: string | null;
    status?: string | null;
    order_id?: string | null;
    payload?: unknown;
    /** true/false when Steadfast signed the call, null when it didn't. */
    signature_valid?: boolean | null;
    result:
        | "processed"
        | "no_change"
        | "recorded"
        | "duplicate"
        | "order_not_found"
        | "invalid"
        | "bad_signature"
        | "error";
    result_detail?: string | null;
    http_status: number;
    create_time: string;
}

/**
 * A phone number's delivery record across all Steadfast merchants. Ratios
 * are whole percents of finished parcels and are null — unknown, not a
 * clean record — when nothing has finished for the number. Exact counts
 * are null unless we're booking with Steadfast regularly.
 */
export interface DeliveryRecord {
    phone?: string | null;
    delivery_ratio?: number | null;
    cancellation_ratio?: number | null;
    volume_band?: string | null;
    volume_range?: string | null;
    delivered_count?: number | null;
    cancelled_count?: number | null;
    total_reports?: number | null;
    /** What other merchants' reports were about: code -> times reported. */
    fraud_categories: Record<string, number>;
    doubtful_reports?: boolean | null;
}

/** One step a Steadfast parcel has been through. */
export interface ParcelTrackingStep {
    text: string;
    /** ISO time, as Steadfast reports it. */
    created_at?: string | null;
    tracking_type?: number | null;
    consignment_id?: string | null;
}

export interface ParcelTracking {
    /** The invoice looked up — the order number. */
    invoice: string;
    steps: ParcelTrackingStep[];
}

export interface CourierWebhookEventQuery {
    notification_type?: string;
    search?: string;
    order_id?: string;
    skip?: number;
    limit?: number;
}

/**
 * Steadfast parcel booking. Goes through our API, which holds the Steadfast
 * keys — they used to be built into this app's public JavaScript.
 */
export class CourierService {
    /** Recent webhook calls from Steadfast, newest first. */
    async getWebhookEvents(
        query: CourierWebhookEventQuery = {},
    ): Promise<{ items: CourierWebhookEvent[]; total: number }> {
        const params: Record<string, string | number> = {};
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== "") params[key] = value;
        });
        return apiService.get(`/orders/webhook/events`, { params });
    }

    /** Every step the order's Steadfast parcel has been through (not stored). */
    async getParcelTracking(orderId: string): Promise<ParcelTracking> {
        return apiService.get<ParcelTracking>(`/orders/${orderId}/courier/steadfast/tracking`, {
            timeout: 45000,
        });
    }

    /**
     * A customer's delivery record from Steadfast. Each call spends a small
     * allowance on Steadfast's side, so only call it when staff ask.
     */
    async checkDeliveryRecord(phone: string): Promise<DeliveryRecord> {
        return apiService.post<DeliveryRecord>(
            `/orders/courier/steadfast/delivery-record`,
            { phone },
            { timeout: 45000 },
        );
    }

    /**
     * Book the order with Steadfast. The server saves the consignment on the
     * order in the same step, and answers with Steadfast's own response.
     */
    async createCourierOrder(
        orderId: string,
        orderData: CreateCourierOrderRequest,
    ): Promise<SteadfastCourierResponse> {
        return apiService.post<SteadfastCourierResponse>(
            `/orders/${orderId}/courier/steadfast`,
            orderData,
            // Steadfast can be slow; the server gives it up to 30 s.
            { timeout: 45000 },
        );
    }

    /**
     * Map order data to courier format
     */
    mapOrderToCourierData(order: any, customer: any): CreateCourierOrderRequest {
        console.log('Mapping order to courier data:', order);
        console.log('Order items:', order.items);

        // Generate item description: "{qty} x {name}" per line
        let itemDescription = 'Order items';

        if (order.items && Array.isArray(order.items) && order.items.length > 0) {
            itemDescription = order.items
                .map((item: any) => {
                    const qty = item.quantity || 1;
                    const name = item.product?.name || item.name || 'Product';
                    return `${qty} x ${name}`;
                })
                .join(',\n');
        }

        // Get customer full name
        const recipientName = customer
            ? getCustomerFullName(customer) || 'Walk-in Customer'
            : 'Walk-in Customer';

        // Format phone number (ensure 11 digits)
        const recipientPhone = this.formatPhoneNumber(customer?.phone);

        // Alternative phone — only populate if customer has one
        const altPhone = customer?.alternative_phone?.trim()
            ? this.formatPhoneNumber(customer.alternative_phone)
            : undefined;

        // Pre-fill note with alternative number if available
        const note = altPhone ? `Alternative number: ${altPhone}` : '';

        // Compute due amount from payment transactions (same logic as OrderDetailDrawer)
        const totalAmount = Number(order.total_amount) || 0;
        const transactions: any[] = Array.isArray(order.payment_transactions)
            ? order.payment_transactions
            : [];
        const amountPaid = transactions.reduce(
            (sum: number, t: any) => sum + Number(t.amount),
            0,
        );
        const codAmount = Math.max(
            0,
            Math.round((totalAmount - amountPaid) * 100) / 100,
        );

        return {
            invoice: order.order_number,
            recipient_name: recipientName,
            recipient_phone: recipientPhone,
            alternative_phone: altPhone,
            recipient_address: order.shipping_address || 'Address not provided',
            cod_amount: codAmount,
            note,
            item_description: itemDescription,
            delivery_type: 0, // Default to home delivery
        };
    }

    /**
     * Format phone number to 11 digits for Steadfast dispatch.
     *
     * Tries the real BD normalization first (correctly handles a +880/880
     * prefix, unlike the digit-stripping fallback below, which used to be
     * the only logic here and silently mangled +880 numbers — e.g.
     * "+8801712345678" became "88017123456" instead of "01712345678").
     * Falls back to that best-effort digit-truncate/pad on anything that
     * doesn't normalize (legacy/malformed stored data with no live user
     * to fix it) so dispatch never breaks outright for an old order.
     */
    private formatPhoneNumber(phone?: string): string {
        if (!phone) return '01234567890'; // Default fallback

        const normalized = tryNormalizeBdPhone(phone);
        if (normalized) return normalized;

        // Remove any non-digit characters
        const cleaned = phone.replace(/\D/g, '');

        // Ensure it's 11 digits
        if (cleaned.length === 11) {
            return cleaned;
        } else if (cleaned.length === 10) {
            return '0' + cleaned; // Add leading zero if missing
        } else if (cleaned.length < 11) {
            return cleaned.padEnd(11, '0'); // Pad with zeros if too short
        } else {
            return cleaned.substring(0, 11); // Truncate if too long
        }
    }
}

export const courierService = new CourierService();
